import { apiFetch } from './client';
import { MailTemplateType, TemplateType } from '../consts';
import { sanitizeEmailHtml } from '../utils/sanitizeHtml';
import {
  assertMailTemplateList,
  assertSmsTemplateList,
  assertTemplateCreateResponse,
} from './guards';

export interface MailTemplateItem {
  id: number;
  name: string;
  subject: string;
  body: string;
  mailTemplateType: MailTemplateType;
  tags: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SmsTemplateItem {
  id: number;
  name: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateListParams {
  type: TemplateType;
  mailTemplateType?: MailTemplateType;
  signal?: AbortSignal;
}

export interface CreateMailTemplateRequest {
  type: typeof TemplateType.MAIL;
  name: string;
  body: string;
  subject: string;
  mailTemplateType: MailTemplateType;
  tags: string | null;
}

export interface CreateSmsTemplateRequest {
  type: typeof TemplateType.SMS;
  name: string;
  body: string;
}

export type CreateTemplateRequest = CreateMailTemplateRequest | CreateSmsTemplateRequest;

export interface UpdateMailTemplateRequest {
  id: number;
  type: typeof TemplateType.MAIL;
  name: string;
  body: string;
  subject: string;
  mailTemplateType: MailTemplateType;
  tags: string | null;
}

export interface UpdateSmsTemplateRequest {
  id: number;
  type: typeof TemplateType.SMS;
  name: string;
  body: string;
}

export type UpdateTemplateRequest = UpdateMailTemplateRequest | UpdateSmsTemplateRequest;

export interface TemplateCreateResponse {
  id: number;
}

/**
 * GET /api/v1/template
 * Returns all templates of the specified type, optionally filtered by mailTemplateType.
 */
export async function getTemplates(
  params: TemplateListParams,
): Promise<MailTemplateItem[] | SmsTemplateItem[]> {
  const query = new URLSearchParams({ type: String(params.type) });
  if (params.mailTemplateType !== undefined) {
    query.set('mailTemplateType', String(params.mailTemplateType));
  }
  const fetchOptions: RequestInit = {};
  if (params.signal !== undefined) {
    fetchOptions.signal = params.signal;
  }
  const data = await apiFetch<unknown>(`/api/v1/template?${query.toString()}`, fetchOptions);
  if (params.type === TemplateType.MAIL) {
    assertMailTemplateList(data);
    // Sanitize mail body once here (the API boundary) so consumers can render it
    // via dangerouslySetInnerHTML without re-sanitizing on every render.
    return data.map((item) => ({ ...item, body: sanitizeEmailHtml(item.body) }));
  }
  assertSmsTemplateList(data);
  return data;
}

/**
 * POST /api/v1/template
 * Creates a new template. Returns the id of the newly created record.
 */
export async function createTemplate(body: CreateTemplateRequest): Promise<TemplateCreateResponse> {
  const data = await apiFetch<unknown>('/api/v1/template', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  assertTemplateCreateResponse(data);
  return data;
}

/**
 * PUT /api/v1/template
 * Updates an existing template. Returns an empty object on success.
 */
export async function updateTemplate(body: UpdateTemplateRequest): Promise<void> {
  await apiFetch<void>('/api/v1/template', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/v1/template/:id?type=...
 * Deletes a template by id and type. Returns an empty object on success.
 */
export async function deleteTemplate(id: number, type: TemplateType): Promise<void> {
  await apiFetch<void>(`/api/v1/template/${id}?type=${type}`, {
    method: 'DELETE',
  });
}
